import type { Ref } from "react";
import { Entity } from "@common/types/entity";
import { pluralizeWithCount } from "@common/helpers/string";
import { Picker } from "@design-system/Picker/Picker.lib";
import { ariaLabelledbyProps } from "@design-system/formControl/ariaLabelledbyProps";
import { EntityAvatarGroup } from "@components/avatar/EntityAvatarGroup";
import { EntityFilterProps, NO_ENTITY_LABEL } from "./types";

interface EntityFilterTriggerProps extends Pick<
    EntityFilterProps,
    | "renderTrigger"
    | "showLabel"
    | "triggerButtonProps"
    | "invalid"
    | "aria-labelledby"
    | "onBlur"
> {
    selectedEntities: Entity[];
    disabled: boolean;
    /** The "No entity" row is the whole selection: the button reads its label instead of avatars. */
    isOnlyNoEntitySelected?: boolean;
    ref?: Ref<HTMLButtonElement>;
}

/**
 * The entity picker trigger, placed inside its `Picker.Select`: the caller's
 * `renderTrigger` button, or the default entity icon list on a primary md
 * button without a chevron. Both carry the picker's ref, blur, invalid state
 * and `aria-labelledby`.
 */
export const EntityFilterTrigger = ({
    renderTrigger,
    selectedEntities,
    disabled,
    showLabel = true,
    triggerButtonProps,
    invalid,
    isOnlyNoEntitySelected = false,
    "aria-labelledby": ariaLabelledby,
    ref,
    onBlur,
}: EntityFilterTriggerProps) => {
    if (renderTrigger) {
        return (
            <Picker.Trigger
                ref={ref}
                render={(triggerProps) =>
                    renderTrigger({
                        triggerProps: {
                            ...triggerProps,
                            ...ariaLabelledbyProps(ariaLabelledby),
                            disabled,
                            "aria-invalid":
                                invalid || triggerProps["aria-invalid"],
                            onBlur: (event) => {
                                triggerProps.onBlur?.(event);
                                onBlur?.();
                            },
                        },
                        selectedEntities,
                    })
                }
            />
        );
    }

    const { tooltip, ...buttonProps } = triggerButtonProps ?? {};
    const entityNames = isOnlyNoEntitySelected
        ? NO_ENTITY_LABEL
        : selectedEntities
              .map((entity) => entity.name ?? "")
              .filter((name) => name !== "")
              .join(", ");

    return (
        <Picker.ButtonTrigger
            ref={ref}
            render={
                <Picker.Button
                    aria-label={entityNames === "" ? undefined : entityNames}
                    variant="primary"
                    disabled={disabled}
                    {...buttonProps}
                    size={buttonProps.size ?? "md"}
                    {...ariaLabelledbyProps(ariaLabelledby)}
                    onBlur={() => onBlur?.()}
                />
            }
            value={
                isOnlyNoEntitySelected ? (
                    NO_ENTITY_LABEL
                ) : (
                    <EntityAvatarGroup
                        entities={selectedEntities}
                        max={3}
                        size="xs"
                        label={
                            showLabel
                                ? pluralizeWithCount(
                                      "Entity",
                                      selectedEntities.length,
                                  )
                                : undefined
                        }
                    />
                )
            }
            tooltip={tooltip}
            showChevron={false}
        />
    );
};
